import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { EnvelopeSimple, CheckCircle } from "@phosphor-icons/react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Could not send reset link"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <Link to="/" className="font-display text-2xl font-extrabold text-slate-900 block mb-8">
          Shift<span className="text-blue-600">Flex</span>
        </Link>
        <div className="bg-white border border-slate-200 rounded-md p-8">
          {!sent ? (
            <>
              <h1 className="font-display text-3xl font-extrabold text-slate-900 mb-1">Forgot password?</h1>
              <p className="text-slate-600 mb-6">Enter the email you signed up with and we'll send a reset link.</p>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label htmlFor="email" className="label-caps">Email</Label>
                  <Input
                    data-testid="forgot-email-input"
                    id="email" type="email" required autoComplete="email"
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    className="h-12 mt-1.5"
                  />
                </div>
                <Button
                  data-testid="forgot-submit-btn"
                  type="submit" disabled={busy}
                  className="w-full h-12 bg-blue-600 hover:bg-blue-700 rounded-md font-semibold text-base"
                >
                  <EnvelopeSimple size={18} className="mr-2" />
                  {busy ? "Sending link…" : "Send reset link"}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center py-6" data-testid="forgot-sent-message">
              <div className="w-14 h-14 mx-auto rounded-full bg-emerald-50 flex items-center justify-center mb-4">
                <CheckCircle size={32} weight="fill" className="text-emerald-600" />
              </div>
              <h2 className="font-display text-2xl font-extrabold text-slate-900 mb-2">Check your inbox</h2>
              <p className="text-slate-600">
                If an account exists for <b>{email}</b>, a password-reset link is on its way. It's valid for 1 hour and can only be used once.
              </p>
            </div>
          )}
          <div className="mt-6 text-sm text-slate-600 text-center">
            Remembered it? <Link to="/login" data-testid="link-back-to-login" className="text-blue-600 font-semibold">Back to login</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
